import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import logo from '../assets/images/logo2.png';

import * as authAction from "../actions/auth";
import { useProfile } from '../components/userflow/components/context/ProfileContext';

export default function Verify() {
    const navigate = useNavigate();
    const { token } = useParams();
    const { fetchProfile } = useProfile();

    const [error, setError] = useState('');
    const [verified, setVerified] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Animation States
    const [isEntering, setIsEntering] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    // Load Tailwind for premium styling
    useEffect(() => {
        if (!document.getElementById("tailwind-script")) {
            const script = document.createElement("script");
            script.id = "tailwind-script";
            script.src = "https://cdn.tailwindcss.com?plugins=forms,container-queries";
            script.onload = () => setIsTailwindLoaded(true);
            document.head.appendChild(script);
        } else {
            setIsTailwindLoaded(true);
        }
    }, []);

    useEffect(() => {
        const verifyToken = async () => {
            try {
                const response = await authAction.getVerifyToken(token);
                if (response) {
                    setVerified(true);
                    setShowSuccess(true);
                    
                    // Start Animation Sequence
                    setTimeout(() => setIsEntering(true), 100);
                    
                    // Redirection sequence
                    setTimeout(() => setIsLeaving(true), 3200);
                    setTimeout(async () => {
                        await fetchProfile();
                        navigate("/abtyourself");
                    }, 4000);

                } else {
                    setError(response?.message || 'Verification failed.');
                }
            } catch (err) {
                setError('An error occurred while verifying your email address. Please try again.');
                console.error('Verification error:', err);
            } finally {
                setLoading(false);
            }
        };

        if (token) verifyToken();
    }, [token, fetchProfile, navigate]);

    if (!isTailwindLoaded) return null;

    return (
        <div className="min-h-screen bg-[#f8f6f5] dark:bg-[#23140f] flex flex-col items-center justify-center font-['Inter',_sans-serif] p-6 relative overflow-hidden">

            {/* Error View */}
            {error && (
                <div className="text-center max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-red-600 text-4xl">!</span>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Oops!</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">{error}</p>
                    <button 
                        onClick={() => navigate('/login')}
                        className="px-8 py-3 bg-[#111111] dark:bg-white text-white dark:text-[#111111] rounded-full font-bold transition-transform hover:scale-105"
                    >
                        Back to Login
                    </button>
                </div>
            )}

            {/* Verification Loading View */}
            {loading && !verified && !error && (
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="animate-spin text-[#ff794d]" size={48} />
                    <p className="text-slate-500 dark:text-slate-400 font-semibold tracking-wide animate-pulse">
                        Verifying your identity...
                    </p>
                </div>
            )}

            {/* Premium Success Animation Overlay */}
            {showSuccess && (
                <div className={`absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#f8f6f5] dark:bg-[#23140f] transition-all duration-1000 ease-in-out ${isLeaving ? 'opacity-0 backdrop-blur-none' : 'opacity-100 backdrop-blur-sm'}`}>
                    <div className={`flex flex-col items-center px-8 text-center transition-all duration-1000 cubic-bezier(0.34, 1.56, 0.64, 1) ${!isEntering ? 'scale-75 opacity-0 translate-y-12' : isLeaving ? 'scale-95 opacity-0 -translate-y-8' : 'scale-100 opacity-100 translate-y-0'}`}>
                        <div className="w-24 h-24 lg:w-32 lg:h-32 bg-[#ff794d]/10 rounded-full flex items-center justify-center mb-8 animate-bounce transition-transform duration-1000">
                            <ShieldCheck className="text-[#ff794d] lg:scale-150 transition-transform" size={56} strokeWidth={2.5} />
                        </div>
                        <h2 className="text-4xl lg:text-6xl font-black tracking-tight text-slate-900 dark:text-white mb-3 drop-shadow-sm leading-tight">
                            Email Verified!
                        </h2>
                        <p className="text-slate-500 dark:text-slate-400 font-semibold text-xl lg:text-2xl tracking-tight leading-relaxed max-w-md mx-auto">
                            Fantastic! Your account is now fully secured and active.
                        </p>
                        <div className="mt-8 flex items-center gap-3">
                            <div className="w-2 h-2 bg-[#ff794d] rounded-full animate-ping"></div>
                            <span className="text-sm font-bold text-[#ff794d] uppercase tracking-[0.2em]">Redirecting...</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
